import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { useState } from "react";
import { APIKey } from "../../types/app.type";
import APIKeysTableBody from "./APIKeysTableBody";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

function APIKeysTable<TData extends APIKey, TValue>({
  columns,
  data
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: "includesString",
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    state: { sorting, globalFilter }
  });

  const sortingFilteringInUse = sorting.length || globalFilter;

  return (
    <div className="flex flex-col max-h-[40vh] w-full">
      <div className="flex gap-3 items-center pb-4">
        <Input
          name="APIkeySearch"
          placeholder="Search for key..."
          data-testid="APIkeySearch"
          value={globalFilter || ""}
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          className="max-w-sm"
        />
        {sortingFilteringInUse && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="resetFilter"
            onClick={() => {
              table.resetGlobalFilter();
              table.resetSorting();
            }}
          >
            Reset table
          </Button>
        )}
      </div>

      <div className="rounded border overflow-y-auto w-full">
        <Table>
          <colgroup>
            <col width="100%" />
            <col width="100%" />
            <col width="0%" />
            <col width="0%" />
          </colgroup>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            <APIKeysTableBody table={table} />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default APIKeysTable;
